import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, Search, Settings, Users } from 'lucide-react';
import MemberAvatar from './ui/MemberAvatar';
import organizationsService from '../services/organizationsService';
import TeamAvatar from './ui/TeamAvatar';
import { initials, tileHue } from './OrganizationsList';

/**
 * One organisation, as a directory of its TEAMS.
 *
 * The teams are what people come for; the organisation's own settings are one
 * button away rather than the first thing on screen. That is the difference
 * between a page you pass through daily and a settings form you visit twice.
 *
 * Member avatars sit on each row because "who is in this team" is the question
 * a list of teams is usually being scanned for, and a count does not answer it.
 */

const ROLE_FILTERS = [
  { key: 'owner', label: 'Owned by me' },
  { key: 'admin', label: 'Admin' },
  { key: 'member', label: 'Member' },
];

// Beyond this many faces a row stops being scannable and starts being a smear.
const MAX_FACES = 3;

export default function OrganizationDetail({
  organization,
  onBack,
  onOpenSettings,
  onOpenTeam,
  onCreateTeam,
  memberCount,
}) {
  const [teams, setTeams] = useState([]);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setTeams(await organizationsService.getTeams(organization.id));
    } catch (e) {
      setError(e.message);
    }
  }, [organization.id]);

  useEffect(() => {
    load();
  }, [load]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return teams.filter((t) => {
      if (role && t.role !== role) return false;
      return !q || (t.name || '').toLowerCase().includes(q);
    });
  }, [teams, query, role]);

  const canAdminister = organization.role === 'owner' || organization.role === 'admin';

  return (
    <div className="orgx-scroll">
      <div className="orgx">
      <div className="orgx-crumbs">
        <button type="button" className="orgx-crumb-link" onClick={onBack}>
          Organizations
        </button>
        <ChevronRight size={13} aria-hidden />
        <span>{organization.name}</span>
      </div>

      <div className="orgx-head">
        <div className="orgx-head-id">
          <span className="orgx-tile orgx-tile--lg" style={{ '--tile-h': tileHue(organization.id) }} aria-hidden>
            {initials(organization.name)}
          </span>
          <div>
            <h1 className="orgx-title orgx-title--md">{organization.name}</h1>
            <p className="orgx-sub">
              {teams.length} team{teams.length === 1 ? '' : 's'}
              {memberCount != null && ` · ${memberCount} member${memberCount === 1 ? '' : 's'}`}
              {` · ${organization.role}`}
            </p>
          </div>
        </div>

        <div className="orgx-head-actions">
          {canAdminister && (
            <button type="button" className="orgx-btn" onClick={onOpenSettings}>
              <Settings size={14} aria-hidden /> Org settings
            </button>
          )}
          {canAdminister && onCreateTeam && (
            <button type="button" className="orgx-btn orgx-btn--primary" onClick={onCreateTeam}>
              <Plus size={15} aria-hidden /> Create team
            </button>
          )}
        </div>
      </div>

      <div className="orgx-toolbar">
        <div className="orgx-search">
          <Search size={14} aria-hidden />
          <input
            value={query}
            placeholder={`Search teams in ${organization.name}`}
            aria-label="Search teams"
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <div className="orgx-filters">
          {ROLE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`orgx-filter${role === f.key ? ' orgx-filter--on' : ''}`}
              aria-pressed={role === f.key}
              onClick={() => setRole((r) => (r === f.key ? null : f.key))}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="orgx-error">{error}</p>}

      {teams.length === 0 && !error && (
        <p className="orgx-none">
          No teams attached yet. Attaching a team lets this organization's admins
          administer it — its members, workspaces and boards stay exactly where they are.
        </p>
      )}

      <div className="orgx-rows">
        {visible.map((t) => {
          const faces = (t.members || []).slice(0, MAX_FACES);
          const extra = (t.members || []).length - faces.length;
          return (
            <button
              key={t.id}
              type="button"
              className="orgx-row"
              onClick={() => onOpenTeam?.(t)}
            >
              <TeamAvatar team={t} size={42} />

              <span className="orgx-row-texts">
                <span className="orgx-card-line">
                  <span className="orgx-card-name">{t.name}</span>
                  {t.role && <span className={`orgx-role orgx-role--${t.role}`}>{t.role}</span>}
                </span>
                <span className="orgx-card-meta">
                  {t.workspace_count != null &&
                    `${t.workspace_count} workspace${t.workspace_count === 1 ? '' : 's'} · `}
                  {t.member_count != null && `${t.member_count} members`}
                  {t.inserted_at && ` · ${new Date(t.inserted_at).toLocaleDateString('en-GB')}`}
                </span>
              </span>

              {/* Faces, not a number: "who is in this team" is what a list of
                  teams is scanned for, and each one opens the same profile
                  card as chat and boards. */}
              <span className="orgx-faces">
                {faces.map((m) => (
                  <MemberAvatar
                    key={m.id ?? m.user_id}
                    userId={m.id ?? m.user_id}
                    name={m.name}
                    email={m.email}
                    size={26}
                  />
                ))}
                {extra > 0 && <span className="orgx-faces-more">+{extra}</span>}
                {faces.length === 0 && (
                  <span className="orgx-faces-none">
                    <Users size={13} aria-hidden />
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
      </div>
    </div>
  );
}
